import React, { useEffect, useRef, useState } from "react";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import { TenantDeletionSpinner } from "./TenantDeletionSpinner";

export interface DeleteTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: { reason: string; tenantNameConfirmation: string }) => void;
  tenant: { id: string; name: string };
  isPending: boolean;
}

const REASON_MIN = 20;
const REASON_MAX = 500;

/**
 * Modal that triggers Phase 1 of tenant deletion (AC-31).
 *
 * Behavior:
 * - Disabled submit button until both reason (20-500) and tenantNameConfirmation match (case-insensitive)
 * - role="dialog", aria-modal="true", aria-labelledby="delete-tenant-title"
 * - Focus trap, focus on reason input on open
 * - ESC closes only when not pending; backdrop click blocked while pending
 * - ESC / backdrop / Anuluj prompts `window.confirm(...)` when form has unsaved data
 * - Counter (decorative, aria-live="off") formatted "X/500"
 * - Spinner + "Usuwanie..." while pending
 * - No emoji/icon in heading; copy: "Operacja jest nieodwracalna."
 */
export function DeleteTenantModal({
  isOpen,
  onClose,
  onSubmit,
  tenant,
  isPending,
}: DeleteTenantModalProps) {
  const [reason, setReason] = useState("");
  const [tenantNameConfirmation, setTenantNameConfirmation] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Focus reason on open
  useEffect(() => {
    if (isOpen) {
      // micro-delay so the textarea is mounted
      const t = setTimeout(() => reasonRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setTenantNameConfirmation("");
    }
  }, [isOpen]);

  // Confirm before discarding unsaved data (ESC, backdrop, X / Cancel button).
  // Returns true if it's safe to close, false if user cancelled the close.
  const confirmCloseIfDirty = (): boolean => {
    const hasUnsavedInput =
      reason.length > 0 || tenantNameConfirmation.length > 0;
    if (!hasUnsavedInput) return true;
    return window.confirm(
      "Czy na pewno chcesz zamknąć formularz? Wprowadzone dane zostaną utracone."
    );
  };

  const handleCloseAttempt = () => {
    if (!confirmCloseIfDirty()) return;
    onClose();
  };

  // ESC handler — only when NOT pending
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) {
        handleCloseAttempt();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPending, onClose, reason, tenantNameConfirmation]);

  useFocusTrap(containerRef, isOpen);

  if (!isOpen) return null;

  const reasonLength = reason.length;
  const reasonValid =
    reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;
  const nameValid =
    tenantNameConfirmation.trim().toLowerCase() ===
    tenant.name.trim().toLowerCase();
  const canSubmit = reasonValid && nameValid && !isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ reason, tenantNameConfirmation });
  };

  // Backdrop click: block while pending; otherwise prompt if form is dirty.
  const handleBackdropClick = () => {
    if (isPending) return;
    handleCloseAttempt();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
        data-testid="delete-tenant-backdrop"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-tenant-title"
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
          data-testid="delete-tenant-modal"
        >
          <form onSubmit={handleSubmit}>
            {/* Header — minimalist, no emoji/icon */}
            <div className="border-b border-neutral-200 px-6 py-4">
              <h2
                id="delete-tenant-title"
                className="text-lg font-semibold text-neutral-900"
              >
                Usuń tenanta
              </h2>
              <p className="mt-1 text-sm text-neutral-600">
                Operacja jest nieodwracalna.
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label
                  htmlFor="delete-tenant-reason"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Powód usunięcia
                </label>
                <textarea
                  ref={reasonRef}
                  id="delete-tenant-reason"
                  data-testid="delete-tenant-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={isPending}
                  rows={4}
                  maxLength={REASON_MAX}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                  placeholder="Min. 20 znaków…"
                />
                <div
                  data-testid="delete-tenant-reason-counter"
                  aria-live="off"
                  className={`mt-1 text-xs ${
                    reasonLength > 0 && reasonLength < REASON_MIN
                      ? "text-red-600"
                      : "text-neutral-500"
                  }`}
                >
                  {reasonLength}/{REASON_MAX}
                </div>
              </div>

              <div>
                <label
                  htmlFor="delete-tenant-name-confirmation"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Wpisz nazwę tenanta: &quot;{tenant.name}&quot; aby potwierdzić
                </label>
                <input
                  type="text"
                  id="delete-tenant-name-confirmation"
                  data-testid="delete-tenant-name-confirmation"
                  value={tenantNameConfirmation}
                  onChange={(e) => setTenantNameConfirmation(e.target.value)}
                  disabled={isPending}
                  autoComplete="off"
                  aria-describedby={
                    nameValid ? "delete-tenant-name-match" : undefined
                  }
                  aria-invalid={
                    tenantNameConfirmation.length > 0 && !nameValid
                  }
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-neutral-100 disabled:cursor-not-allowed ${
                    nameValid
                      ? "border-green-500 focus:ring-green-500 focus:border-green-500"
                      : "border-neutral-300 focus:ring-red-500 focus:border-red-500"
                  }`}
                />
                {nameValid && (
                  <p
                    id="delete-tenant-name-match"
                    data-testid="delete-tenant-name-match"
                    className="mt-1 text-xs text-green-600 flex items-center gap-1"
                  >
                    <span aria-hidden="true">✓</span>
                    <span>Nazwa pasuje</span>
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseAttempt}
                disabled={isPending}
                data-testid="delete-tenant-cancel"
                className="rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="delete-tenant-submit"
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  canSubmit
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                }`}
              >
                {isPending && (
                  <TenantDeletionSpinner
                    data-testid="delete-tenant-spinner"
                    size="sm"
                    variant="onAccent"
                  />
                )}
                {isPending ? "Usuwanie…" : "Usuń"}
              </button>
              <span
                role="status"
                aria-live="assertive"
                data-testid="delete-tenant-status-live"
                className="sr-only"
              >
                {isPending ? "Trwa usuwanie tenanta..." : ""}
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DeleteTenantModal;
